import React, { Fragment } from 'react';
import {
  styled,
  DripsyCustomTheme,
  useDripsyTheme,
  useSx,
  SxProp,
} from 'dripsy';
import { observer } from 'mobx-react-lite';
import { View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import {
  EdgeInsets,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';

import { PlayerSlider } from './PlayerSlider';
import { IconTouchable } from '../../common/IconTouchable';
import { PlayerTimeText } from './PlayerTimeText';
import { IS_WEB } from '@utils';
import { WebPlayerControl } from './WebPlayerControl';
import { MobilePlayerControl } from './MobilePlayerControl';
import { SubtitlesType } from './SubtitlesText';
import { Separator } from '../../common/Separator';
import { SubtitlesMenu } from './SubtitlesMenu';
import type { IVideoPlayerStore } from './VideoPlayer';
import { SpaceType } from '@types';
import { TimestampText, TimestampType } from './TimestampText';
import { PlaybackRateMenu } from './PlaybackRateMenu';

const BackgroundGradient = styled(LinearGradient)({
  zIndex: 1,
  position: 'absolute',
  width: '100%',
  height: '100%',
});

const MobilePlayerControlContainer = styled(View)({
  position: 'absolute',
  zIndex: 1,
  height: '100%',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center',
});

const MobilePlayerControlBackground = styled(View)({
  opacity: 0.5,
  position: 'absolute',
  height: '100%',
  width: '100%',
  bg: '$black',
});

const containerStyle: SxProp = {
  position: 'absolute',
  zIndex: 1,
  width: '100%',
  height: '100%',
};

interface IBottomControlContainerStyleProps {
  insets: EdgeInsets;
  isInFullScreen: boolean;
  spaces: DripsyCustomTheme['space'];
}

const BottomControlContainer = styled(View)(
  (props: IBottomControlContainerStyleProps) => {
    let pb: number | SpaceType = IS_WEB ? '$7' : '$4';

    if (props.isInFullScreen) {
      pb = props.spaces.$6 + props.insets.bottom;
    }

    return {
      position: 'absolute',
      bottom: 0,
      pb,
      px: IS_WEB ? '$8' : '$4',
      zIndex: 1,
      width: '100%',
    };
  },
);

interface ISubtitleControlContainerStyleProps {
  insets: EdgeInsets;
  isInFullScreen: boolean;
}

const subtitleControlContainerStyle = (
  props: ISubtitleControlContainerStyleProps,
): SxProp => {
  return (theme) => {
    let pt: number | SpaceType = IS_WEB ? '$7' : '$5';

    if (props.isInFullScreen) {
      pt = theme.space.$5 + props.insets.top;
    }

    return {
      position: 'absolute',
      top: 0,
      right: 0,
      pt,
      px: IS_WEB ? '$8' : '$4',
      zIndex: 1,
      flexDirection: 'row',
    };
  };
};

const topProgressContainerStyle: SxProp = {
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexDirection: 'row',
};

const VerticalLine = styled(View)({
  bg: '$background0',
  width: 1,
  height: '100%',
});

const RowContainer = styled(View)({
  flexDirection: 'row',
  flex: 1,
});

export interface IVideoPlayerControlProps {
  hasNextVideo: boolean;
  hasPreviousVideo: boolean;
  onNextVideo(): void;
  onPreviousVideo(): void;
  onToggleFullScreen(): void;
  onTogglePlay(): void;
  onSlidingComplete(value: number): void;
  onSlidingStart(): void;
  isInFullScreen?: boolean;
  subtitles?: SubtitlesType | null;
  store: IVideoPlayerStore;
  progress: Animated.SharedValue<number>;
  max: Animated.SharedValue<number>;
  min: Animated.SharedValue<number>;
  volume: Animated.SharedValue<number>;
  cache: Animated.SharedValue<number>;
  isScrubbing: Animated.SharedValue<boolean>;
  timestamps: TimestampType;
  onPressTimestamp(): void;
  hasSubtitles?: boolean;
}

export const VideoPlayerControl = observer<IVideoPlayerControlProps>(
  ({
    hasNextVideo = false,
    hasPreviousVideo = false,
    onNextVideo,
    onPreviousVideo,
    onToggleFullScreen,
    onSlidingComplete,
    onSlidingStart,
    onTogglePlay,
    store,
    progress,
    max,
    min,
    volume,
    cache,
    isScrubbing,
    timestamps,
    onPressTimestamp,
    hasSubtitles,
  }) => {
    const insets = useSafeAreaInsets();
    const { theme } = useDripsyTheme();
    const sx = useSx();
    const aIsShowControl = store.isShowControl;
    const mobilePlayerControlStyles = useAnimatedStyle(
      () => ({
        opacity: withTiming(aIsShowControl ? 1 : 0, {
          duration: 100,
        }),
      }),
      [aIsShowControl],
    );
    const animatedTopProgressContainerStyles = useAnimatedStyle(
      () => ({
        opacity: withTiming(isScrubbing.value ? 0 : 1),
      }),
      [isScrubbing],
    );

    const fullScreenButtonIconName = store.isInFullScreen
      ? 'small-screen-bold'
      : 'full-screen';

    const hasTimestamps = timestamps.length > 0;

    return (
      <Animated.View
        style={[sx(containerStyle), mobilePlayerControlStyles]}
        pointerEvents="box-none"
      >
        {IS_WEB && (
          <BackgroundGradient
            pointerEvents="none"
            locations={[0.7, 1]}
            colors={theme.linearGradients.videoPlayerControl}
          />
        )}
        {!IS_WEB && store.isVisibleControl && (
          <MobilePlayerControlContainer pointerEvents="box-none">
            <MobilePlayerControlBackground pointerEvents="none" />

            <MobilePlayerControl
              store={store}
              onTogglePlay={onTogglePlay}
              hasNextVideoButton={hasNextVideo}
              hasPreviousVideoButton={hasPreviousVideo}
              onNextVideo={onNextVideo}
              onPreviousVideo={onPreviousVideo}
            />
          </MobilePlayerControlContainer>
        )}

        {!IS_WEB && store.isVisibleControl && (
          <Animated.View
            style={[
              sx(
                subtitleControlContainerStyle({
                  insets,
                  isInFullScreen: store.isInFullScreen,
                }),
              ),
              mobilePlayerControlStyles,
            ]}
          >
            {hasSubtitles && (
              <Fragment>
                <SubtitlesMenu />
                <Separator size={['$7', '$8']} vertical />
              </Fragment>
            )}
            <PlaybackRateMenu />
          </Animated.View>
        )}

        <BottomControlContainer
          insets={insets}
          isInFullScreen={store.isInFullScreen}
          spaces={theme.space}
        >
          {!IS_WEB && store.isVisibleControl && (
            <Fragment>
              <Animated.View
                style={[
                  sx(topProgressContainerStyle),
                  animatedTopProgressContainerStyles,
                ]}
              >
                <RowContainer>
                  <PlayerTimeText
                    progress={progress}
                    maximumValue={max}
                  />
                  {hasTimestamps && (
                    <RowContainer>
                      <Separator vertical />
                      <VerticalLine />
                      <Separator vertical />
                      <TimestampText
                        progress={progress}
                        timestamps={timestamps}
                        onPress={onPressTimestamp}
                      />
                      <Separator vertical />
                    </RowContainer>
                  )}
                </RowContainer>
                <IconTouchable
                  name={fullScreenButtonIconName}
                  color="$background0"
                  onPress={onToggleFullScreen}
                />
              </Animated.View>
              <Separator />
            </Fragment>
          )}

          <PlayerSlider
            progress={progress}
            minimumValue={min}
            maximumValue={max}
            cache={cache}
            onSlidingComplete={onSlidingComplete}
            onSlidingStart={onSlidingStart}
            isScrubbing={isScrubbing}
            disableTrackFollow
            timestamps={timestamps}
          />

          {IS_WEB && (
            <WebPlayerControl
              store={store}
              progress={progress}
              max={max}
              onTogglePlay={onTogglePlay}
              onToggleFullScreen={onToggleFullScreen}
              volume={volume}
              hasNextVideoButton={hasNextVideo}
              hasPreviousVideoButton={hasPreviousVideo}
              onNextVideo={onNextVideo}
              onPreviousVideo={onPreviousVideo}
              timestamps={timestamps}
              onPressTimestamp={onPressTimestamp}
              hasSubtitles={hasSubtitles}
            />
          )}
        </BottomControlContainer>
      </Animated.View>
    );
  },
);
